import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  User,
  MapPin,
  Filter,
  Bell,
  BellOff,
  Settings,
  RefreshCw,
  Download
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { exportToCSV, generateAlertData } from '../../utils/exportUtils';
import { useKioskScans } from '../../hooks/useKioskScans';
import { formatLocationLabel } from '../../data/kioskLocations';
import { fetchAlerts, resolveAlert } from '../../services/api';
import { useTranslation } from '../../i18n';
import { EmailPreferences } from '../../components/alerts/EmailPreferences';

interface Alert {
  id: string;
  type: 'security' | 'missed' | 'system' | 'emergency';
  priority: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  guardId?: string;
  guardName?: string;
  location?: string;
  timestamp: string;
  status: 'active' | 'acknowledged' | 'resolved';
  assignedTo?: string;
  distanceMeters?: number | null;
}

interface AlertsPageProps {
  /**
   * True when rendered as a section of the Compliance page (its permanent home
   * since the standalone Alerts tab was retired — /supervisor/alerts now
   * redirects there). Renders a section heading instead of a page heading.
   */
  embedded?: boolean;
}

export const AlertsPage: React.FC<AlertsPageProps> = ({ embedded = false }) => {
  const { t, lang } = useTranslation();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'acknowledged' | 'resolved'>('all');
  // The feed can run to hundreds of cards; keep it collapsible and height-capped
  // so the sections below it stay reachable.
  const [feedOpen, setFeedOpen] = useState(true);
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'low' | 'medium' | 'high' | 'critical'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'security' | 'missed' | 'system' | 'emergency'>('all');
  const kioskFeed = useKioskScans(7000);
  const kioskAlerts = kioskFeed.scans.filter(scan => scan.status !== "OK");

  useEffect(() => {
    loadAlerts();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data: any = await fetchAlerts({ limit: 200 });
      setAlerts(data.alerts || []);
    } catch (err: any) {
      console.error('Failed to fetch alerts:', err);
      // Try to extract detail from the API error response
      let detail = '';
      try {
        const body = await err?.response?.json?.();
        if (body?.detail) detail = ` (${body.detail})`;
        else if (body?.error) detail = ` (${body.error})`;
      } catch (_) {}
      setError(`${t('alerts.loadFailed', 'Failed to load alerts')}${detail || ` — ${t('alerts.loadFailedHint', 'check that the alerts endpoint is deployed.')}`}`);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const getAlertIcon = (type: Alert['type']) => {
    switch (type) {
      case 'security':
        return <AlertTriangle className="w-5 h-5" />;
      case 'missed':
        return <Clock className="w-5 h-5" />;
      case 'system':
        return <Settings className="w-5 h-5" />;
      case 'emergency':
        return <Bell className="w-5 h-5" />;
    }
  };

  const getPriorityColor = (priority: Alert['priority']) => {
    switch (priority) {
      case 'low':
        return 'text-gray-600';
      case 'medium':
        return 'text-yellow-600';
      case 'high':
        return 'text-orange-600';
      case 'critical':
        return 'text-red-600';
    }
  };

  const getPriorityBadgeClass = (priority: Alert['priority']) => {
    switch (priority) {
      case 'low':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  const getStatusBadgeClass = (status: Alert['status']) => {
    switch (status) {
      case 'active':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'acknowledged':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const getPriorityLabel = (priority: Alert['priority']) => {
    switch (priority) {
      case 'low':
        return t('alerts.low', 'Low');
      case 'medium':
        return t('alerts.medium', 'Medium');
      case 'high':
        return t('alerts.high', 'High');
      case 'critical':
        return t('alerts.critical', 'Critical');
    }
  };

  const getStatusLabel = (status: Alert['status']) => {
    switch (status) {
      case 'active':
        return t('status.active', 'Active');
      case 'acknowledged':
        return t('alerts.acknowledged', 'Acknowledged');
      case 'resolved':
        return t('alerts.resolved', 'Resolved');
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    try {
      await resolveAlert(alertId);
      setAlerts(alerts.map(alert =>
        alert.id === alertId ? { ...alert, status: 'acknowledged' as const } : alert
      ));
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  const handleResolve = async (alertId: string) => {
    try {
      await resolveAlert(alertId);
      setAlerts(alerts.map(alert =>
        alert.id === alertId ? { ...alert, status: 'resolved' as const } : alert
      ));
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter !== 'all' && alert.status !== filter) return false;
    if (priorityFilter !== 'all' && alert.priority !== priorityFilter) return false;
    if (typeFilter !== 'all' && alert.type !== typeFilter) return false;
    return true;
  });

  const alertCounts = {
    active: alerts.filter(a => a.status === 'active').length,
    acknowledged: alerts.filter(a => a.status === 'acknowledged').length,
    resolved: alerts.filter(a => a.status === 'resolved').length,
    critical: alerts.filter(a => a.priority === 'critical').length,
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {embedded ? (
            <h2 className="text-lg font-semibold text-gray-800">{t('alerts.title', 'Security Alerts')}</h2>
          ) : (
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">{t('alerts.title', 'Security Alerts')}</h1>
          )}
          <p className="text-sm text-gray-600 mt-1">{t('alerts.subtitle', 'Missed checkpoints and flagged events that need attention')}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={loadAlerts}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 min-h-[44px]"
            title={t('alerts.refreshTitle', 'Refresh Alerts')}
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">{t('common.refresh', 'Refresh')}</span>
          </button>
          <button
            onClick={() => exportToCSV(generateAlertData(filteredAlerts))}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 min-h-[44px]"
            title={t('alerts.exportTitle', 'Export Alerts Data')}
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">{t('common.export', 'Export')}</span>
          </button>
        </div>
      </div>

      {/* Email alert preferences — collapsed by default, per-site toggles */}
      <EmailPreferences />

      {/* Alert Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('alerts.activeAlerts', 'Active Alerts')}</p>
              <p className="text-xl font-bold text-red-600">{alertCounts.active}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('alerts.acknowledged', 'Acknowledged')}</p>
              <p className="text-xl font-bold text-yellow-600">{alertCounts.acknowledged}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('alerts.resolved', 'Resolved')}</p>
              <p className="text-xl font-bold text-green-600">{alertCounts.resolved}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Bell className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('alerts.critical', 'Critical')}</p>
              <p className="text-xl font-bold text-blue-700">{alertCounts.critical}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Kiosk Exceptions */}
      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('alerts.scanExceptions', 'Patrol scan exceptions')}</h2>
            <p className="text-sm text-gray-600">{t('alerts.scanExceptionsSubtitle', 'Invalid scans or API errors coming from field devices')}</p>
          </div>
          <span className={cn(
            "rounded-md px-3 py-1 text-xs font-semibold",
            kioskAlerts.length ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
          )}>
            {kioskAlerts.length ? t('alerts.issuesDetected', '{n} issues detected').replace('{n}', String(kioskAlerts.length)) : t('alerts.allLocationsHealthy', 'All locations healthy')}
          </span>
        </div>

        <div className="mt-4 space-y-3">
          {kioskFeed.loading ? (
            <p className="text-sm text-gray-600">{t('alerts.noInvalidScans', 'No invalid scans in the latest batch.')}</p>) : (
            kioskAlerts.slice(0, 5).map((scan) => (
              <div key={scan.id} className="flex items-center justify-between rounded-md border border-rose-100 bg-rose-50 px-3 py-2">
                <div>
                  <p className="text-sm font-semibold text-rose-800">
                    {scan.status === "INVALID" ? t('alerts.invalidScan', 'Invalid scan') : t('alerts.apiError', 'API error')} • {formatLocationLabel(scan.location_id)}
                  </p>
                  <p className="text-xs text-rose-700">
                    {t('alerts.guardLabel', 'Guard {name}').replace('{name}', scan.guard_id || t('alerts.unknownGuard', 'unknown'))} — {new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date(scan.timestamp))}
                  </p>
                </div>
                <span className="text-xs font-semibold text-rose-700">{scan.status}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-600 shrink-0" />
          <span className="text-sm font-medium text-gray-700">{t('alerts.filters', 'Filters')}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="text-sm border border-gray-300 rounded-md px-3 py-2 min-h-[44px] w-full"
          >
            <option value="all">{t('alerts.allStatus', 'All Status')}</option>
            <option value="active">{t('status.active', 'Active')}</option>
            <option value="acknowledged">{t('alerts.acknowledged', 'Acknowledged')}</option>
            <option value="resolved">{t('alerts.resolved', 'Resolved')}</option>
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as any)}
            className="text-sm border border-gray-300 rounded-md px-3 py-2 min-h-[44px] w-full"
          >
            <option value="all">{t('alerts.allPriorities', 'All Priorities')}</option>
            <option value="critical">{t('alerts.critical', 'Critical')}</option>
            <option value="high">{t('alerts.high', 'High')}</option>
            <option value="medium">{t('alerts.medium', 'Medium')}</option>
            <option value="low">{t('alerts.low', 'Low')}</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as any)}
            className="text-sm border border-gray-300 rounded-md px-3 py-2 min-h-[44px] w-full"
          >
            <option value="all">{t('alerts.allTypes', 'All Types')}</option>
            <option value="emergency">{t('alerts.typeEmergency', 'Emergency')}</option>
            <option value="security">{t('domain.security', 'Security')}</option>
            <option value="missed">{t('alerts.typeMissedCheckpoint', 'Missed Checkpoint')}</option>
            <option value="system">{t('alerts.typeSystem', 'System')}</option>
          </select>
        </div>
      </div>

      {/* Alerts List — collapsible header + capped scroll area */}
      <button
        onClick={() => setFeedOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-left shadow-sm hover:bg-gray-50"
      >
        <span className="font-semibold text-gray-800">
          {t('alerts.feedTitle', 'Alert Feed')}
          <span className="ml-2 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {filteredAlerts.length}
          </span>
        </span>
        <span className="text-xs font-medium text-gray-500">
          {feedOpen ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}
        </span>
      </button>
      {feedOpen && (
      <div className="max-h-[36rem] space-y-3 overflow-y-auto pr-1">
        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <p className="text-gray-600">{t('alerts.loadingAlerts', 'Loading alerts...')}</p>
          </div>
        ) : error ? (
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-orange-400" />
            <p className="text-orange-700 font-medium">{error}</p>
            <button onClick={loadAlerts} className="mt-3 px-4 py-2 text-sm bg-white border border-orange-300 rounded-md hover:bg-orange-50">
              {t('alerts.tryAgain', 'Try Again')}
            </button>
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <BellOff className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-600">{t('alerts.noneMatchFilters', 'No alerts found matching your filters')}</p>
          </div>
        ) : (
          filteredAlerts.map((alert) => (
            <div key={alert.id} className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="p-4 md:p-6">
                <div className="flex gap-3 md:gap-4">
                  <div className={cn("p-2 rounded-lg shrink-0 self-start", getPriorityColor(alert.priority))}>
                    {getAlertIcon(alert.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-800">{alert.title}</h3>
                      <span className={cn(
                        "px-2 py-0.5 rounded-md text-xs font-medium border",
                        getPriorityBadgeClass(alert.priority)
                      )}>
                        {getPriorityLabel(alert.priority).toUpperCase()}
                      </span>
                      <span className={cn(
                        "px-2 py-0.5 rounded-md text-xs font-medium border",
                        getStatusBadgeClass(alert.status)
                      )}>
                        {getStatusLabel(alert.status)}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 mb-3">
                      {alert.description}
                      {alert.distanceMeters && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-800">
                          {t('alerts.distanceAway', '{n}m away').replace('{n}', String(alert.distanceMeters))}
                        </span>
                      )}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 md:w-4 md:h-4" />
                        {new Intl.DateTimeFormat(lang, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(alert.timestamp))}
                      </div>

                      {alert.guardName && (
                        <div className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5 md:w-4 md:h-4" />
                          {alert.guardName}
                        </div>
                      )}

                      {alert.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4" />
                          {alert.location}
                        </div>
                      )}
                    </div>

                    {alert.assignedTo && (
                      <p className="text-xs text-gray-500 mt-2">
                        {t('alerts.assignedTo', 'Assigned to:')} {alert.assignedTo}
                      </p>
                    )}

                    {/* Action buttons — below content on all screens */}
                    {(alert.status === 'active' || alert.status !== 'resolved') && (
                      <div className="flex gap-2 mt-3">
                        {alert.status === 'active' && (
                          <button
                            onClick={() => handleAcknowledge(alert.id)}
                            className="px-3 py-1.5 text-sm text-yellow-700 bg-yellow-100 hover:bg-yellow-200 rounded-md transition-colors min-h-[44px]"
                          >
                            {t('alerts.acknowledge', 'Acknowledge')}
                          </button>
                        )}
                        {alert.status !== 'resolved' && (
                          <button
                            onClick={() => handleResolve(alert.id)}
                            className="px-3 py-1.5 text-sm text-green-700 bg-green-100 hover:bg-green-200 rounded-md transition-colors min-h-[44px]"
                          >
                            {t('alerts.resolve', 'Resolve')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      )}
    </div>
  );
};
